import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  FormControlLabel,
  Icon,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import type { Theme } from '@mui/material/styles';
import { V4_ICON_BUTTON, V4_ICON_BUTTON_SMALL, V4_OUTLINED_DEFAULT } from '../../theme/v4Compat';
import { ApiKeyStatusChip, Loader } from '../../elements';
import { LoaderSize } from '../../constants/constants';
import { useTranslation } from 'react-i18next';
import { extractApiErrorMessage, notifications } from '../../utils';
import { useConfirmation } from '../../hooks/ConfirmationService';
import { useParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import { ApiService } from '../../hooks';
import {
  HealthSystemDomainSlaughterhouseModelsSlaughterhouseApiKeyViewModel,
  HealthSystemDomainSlaughterhouseModelsSlaughterhouseViewModel,
  HealthSystemDomainDeviceResultReaderViewModel
} from '../../api/HealthSystemApi';
import { GenerateApiKeyDialog } from '../../components';
import { DeviceService } from '../../hooks/DeviceService';
import ReaderHealthBadge from '../../components/ReaderHealthBadge';
import { formatLastSeen } from '../../utils/readerStatus';

const SlaughterhouseManagementView: React.FC<any> = () => {
  const styles = sxStyles(useTheme());
  const apiService = new ApiService();
  const deviceService = new DeviceService();
  const { t } = useTranslation();
  const { id } = useParams();
  const confirmDialog = useConfirmation();

  const [slaughterhouse, setSlaughterhouse] =
    React.useState<HealthSystemDomainSlaughterhouseModelsSlaughterhouseViewModel | null>(null);
  const [loaded, setLoaded] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(true);
  const [isEditing, setIsEditing] = React.useState(false);

  // Editable fields
  const [name, setName] = React.useState('');
  const [code, setCode] = React.useState('');
  const [address, setAddress] = React.useState('');
  const [contactEmail, setContactEmail] = React.useState('');
  const [contactPhone, setContactPhone] = React.useState('');
  const [isActive, setIsActive] = React.useState(true);

  // API Key management
  const [generateDialogOpen, setGenerateDialogOpen] = React.useState(false);

  // Readers
  const [readers, setReaders] = React.useState<HealthSystemDomainDeviceResultReaderViewModel[]>([]);
  const [readersBusy, setReadersBusy] = React.useState(false);

  const loadSlaughterhouse = async () => {
    setIsBusy(true);
    setLoaded(false);

    if (!id) {
      notifications.error('Slaughterhouse ID is undefined.');
      return;
    }

    await apiService.api.slaughterhouses
      .slaughterhousesGetById({ id })
      .then((result) => {
        setSlaughterhouse(result.data);
        resetFieldsToOriginal(result.data);
        setLoaded(true);
      })
      .catch((e) => {
        setLoaded(false);
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const resetFieldsToOriginal = (data: HealthSystemDomainSlaughterhouseModelsSlaughterhouseViewModel) => {
    setName(data.name || '');
    setCode(data.code || '');
    setAddress(data.address || '');
    setContactEmail(data.contactEmail || '');
    setContactPhone(data.contactPhone || '');
    setIsActive(data.isActive ?? true);
  };

  React.useEffect(() => {
    loadSlaughterhouse();
  }, [id]);

  React.useEffect(() => {
    if (!loaded) {
      loadSlaughterhouse();
    }
  }, [loaded]);

  const loadReaders = React.useCallback(async () => {
    if (!id) return;
    setReadersBusy(true);
    try {
      const result = await deviceService.getReaders(0, 100, id);
      setReaders(result.data);
    } catch {
      // non-critical
    } finally {
      setReadersBusy(false);
    }
  }, [id]);

  React.useEffect(() => {
    loadReaders();
  }, [loadReaders]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelClick = () => {
    if (slaughterhouse) {
      resetFieldsToOriginal(slaughterhouse);
    }
    setIsEditing(false);
  };

  const handleSaveClick = () => {
    if (!name || name.trim().length === 0) {
      notifications.error(t('f-val-slaughterhouse-name'));
      return;
    }

    // Email validation if provided
    if (contactEmail && contactEmail.trim().length > 0) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(contactEmail)) {
        notifications.error(t('f-val-email'));
        return;
      }
    }

    if (!id) {
      notifications.error(t('f-com-api-error'));
      return;
    }

    setIsBusy(true);
    apiService.api.slaughterhouses
      .slaughterhousesUpdate(
        { id },
        {
          name: name || null,
          code: code || null,
          address: address || null,
          contactEmail: contactEmail || null,
          contactPhone: contactPhone || null,
          isActive: isActive
        }
      )
      .then(() => {
        notifications.success(t('f-slaughterhouse-updated'));
        setIsEditing(false);
        setLoaded(false); // Reload
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const handleGenerateApiKeyClose = (keyGenerated: boolean) => {
    setGenerateDialogOpen(false);
    if (keyGenerated) {
      setLoaded(false); // Reload to get updated API keys
    }
  };

  const handleToggleApiKeyStatus = (apiKey: HealthSystemDomainSlaughterhouseModelsSlaughterhouseApiKeyViewModel) => {
    const newStatus = !apiKey.isActive;

    confirmDialog({
      dialogId: 'toggle-api-key-status',
      title: t(newStatus ? 'f-slaughterhouse-api-key-activate' : 'f-slaughterhouse-api-key-deactivate'),
      message: t('f-slaughterhouse-api-key-toggle-msg', { name: apiKey.name }),
      alertSeverity: 'warning'
    }).then(() => {
      if (!id || !apiKey.id) {
        notifications.error(t('f-com-api-error'));
        return;
      }

      apiService.api.slaughterhouses
        .slaughterhousesUpdateApiKey({ id, apiKeyId: apiKey.id }, { isActive: newStatus })
        .then(() => {
          notifications.success(t('f-slaughterhouse-api-key-updated'));
          setLoaded(false); // Reload
        })
        .catch((e) => {
          notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        });
    });
  };

  const handleDeleteApiKey = (apiKey: HealthSystemDomainSlaughterhouseModelsSlaughterhouseApiKeyViewModel) => {
    confirmDialog({
      dialogId: 'delete-api-key',
      title: t('f-slaughterhouse-api-key-delete-title'),
      message: t('f-slaughterhouse-api-key-delete-msg', { name: apiKey.name }),
      alertSeverity: 'warning'
    }).then(() => {
      if (!id || !apiKey.id) {
        notifications.error(t('f-com-api-error'));
        return;
      }

      apiService.api.slaughterhouses
        .slaughterhousesDeleteApiKey({ id, apiKeyId: apiKey.id })
        .then(() => {
          notifications.success(t('f-slaughterhouse-api-key-deleted'));
          setLoaded(false); // Reload
        })
        .catch((e) => {
          notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        });
    });
  };

  const isKeyExpired = (expiresAt?: string | null): boolean => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const formatDate = (dateString?: string | null): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <>
      <Container maxWidth={false} sx={{ maxWidth: 1280 }}>
        <Grid container spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Box sx={{ display: 'flex' }}>
              <Icon color="primary" fontSize="large">
                {'factory'}
              </Icon>
              <Box sx={{ ml: 1, pt: 0.5 }}>
                <Typography component="h2" variant="h6" color="primary">
                  {t('f-slaughterhouse-details')}
                </Typography>
              </Box>
            </Box>
            <Box>
              <IconButton
                color="primary"
                size="medium"
                aria-label={t('f-com-reload')}
                onClick={() => setLoaded(false)}
                sx={V4_ICON_BUTTON}
              >
                <Icon>refresh</Icon>
              </IconButton>
            </Box>
          </Box>

          <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

          {loaded && slaughterhouse && (
            <>
              <Grid item xs={12}>
                <Paper sx={styles.card} elevation={1}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">{t('f-com-information')}</Typography>
                        {!isEditing ? (
                          <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<Icon>edit</Icon>}
                            onClick={handleEditClick}
                            disabled={isBusy}
                          >
                            {t('f-com-edit')}
                          </Button>
                        ) : (
                          <Box>
                            <Button
                              variant="outlined"
                              onClick={handleCancelClick}
                              disabled={isBusy}
                              sx={{ ...V4_OUTLINED_DEFAULT, ...styles.mr1 }}
                              color="inherit"
                            >
                              {t('f-com-cancel')}
                            </Button>
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<Icon>save</Icon>}
                              onClick={handleSaveClick}
                              disabled={isBusy}
                            >
                              {t('f-com-save')}
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">
                        {t('f-slaughterhouse-name')}
                      </Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          required
                          disabled={isBusy}
                          variant="standard"
                        />
                      ) : (
                        <Typography sx={styles.bold}>{slaughterhouse.name}</Typography>
                      )}
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">
                        {t('f-slaughterhouse-code')}
                      </Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          value={code}
                          onChange={(e) => setCode(e.target.value)}
                          disabled={isBusy}
                          variant="standard"
                        />
                      ) : (
                        <Typography sx={styles.bold}>{slaughterhouse.code || '-'}</Typography>
                      )}
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="caption" color="textSecondary">
                        {t('f-slaughterhouse-address')}
                      </Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          value={address}
                          onChange={(e) => setAddress(e.target.value)}
                          disabled={isBusy}
                          variant="standard"
                        />
                      ) : (
                        <Typography sx={styles.bold}>{slaughterhouse.address || '-'}</Typography>
                      )}
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">
                        {t('f-slaughterhouse-contact-email')}
                      </Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          type="email"
                          value={contactEmail}
                          onChange={(e) => setContactEmail(e.target.value)}
                          disabled={isBusy}
                          variant="standard"
                        />
                      ) : (
                        <Typography sx={styles.bold}>{slaughterhouse.contactEmail || '-'}</Typography>
                      )}
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">
                        {t('f-slaughterhouse-contact-phone')}
                      </Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          value={contactPhone}
                          onChange={(e) => setContactPhone(e.target.value)}
                          disabled={isBusy}
                          variant="standard"
                        />
                      ) : (
                        <Typography sx={styles.bold}>{slaughterhouse.contactPhone || '-'}</Typography>
                      )}
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">
                        {t('f-com-status')}
                      </Typography>
                      {isEditing ? (
                        <Box sx={{ mt: 1 }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={isActive}
                                onChange={(e) => setIsActive(e.target.checked)}
                                color="primary"
                                disabled={isBusy}
                              />
                            }
                            label={t('f-com-active')}
                          />
                        </Box>
                      ) : (
                        <Typography sx={styles.bold}>
                          {slaughterhouse.isActive ? t('f-com-active') : t('f-com-inactive')}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper sx={styles.card} elevation={1}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">{t('f-slaughterhouse-api-keys')}</Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<Icon>add</Icon>}
                          onClick={() => setGenerateDialogOpen(true)}
                          disabled={isEditing}
                        >
                          {t('f-slaughterhouse-api-key-generate')}
                        </Button>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      {slaughterhouse.apiKeys && slaughterhouse.apiKeys.length > 0 ? (
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>{t('f-slaughterhouse-api-key-name')}</TableCell>
                              <TableCell>{t('f-slaughterhouse-api-key-description')}</TableCell>
                              <TableCell>{t('f-slaughterhouse-api-key-created')}</TableCell>
                              <TableCell>{t('f-slaughterhouse-api-key-expires')}</TableCell>
                              <TableCell>{t('f-slaughterhouse-api-key-last-used')}</TableCell>
                              <TableCell>{t('f-slaughterhouse-api-key-status')}</TableCell>
                              <TableCell align="center">{t('f-com-actions')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {slaughterhouse.apiKeys.map((key, index) => {
                              const expired = isKeyExpired(key.expiresAt);
                              return (
                                <TableRow key={index}>
                                  <TableCell>{key.name}</TableCell>
                                  <TableCell>{key.description || '-'}</TableCell>
                                  <TableCell>{formatDate(key.createdAt)}</TableCell>
                                  <TableCell>{key.expiresAt ? formatDate(key.expiresAt) : t('f-com-never')}</TableCell>
                                  <TableCell>
                                    {key.lastUsedAt ? formatDate(key.lastUsedAt) : t('f-com-never')}
                                  </TableCell>
                                  <TableCell>
                                    <ApiKeyStatusChip isActive={key.isActive} expiresAt={key.expiresAt} />
                                  </TableCell>
                                  <TableCell align="center">
                                    <Tooltip
                                      title={
                                        expired
                                          ? t('f-com-expired')
                                          : key.isActive
                                            ? t('f-slaughterhouse-api-key-deactivate')
                                            : t('f-slaughterhouse-api-key-activate')
                                      }
                                      arrow
                                    >
                                      <span>
                                        <IconButton
                                          size="small"
                                          onClick={() => handleToggleApiKeyStatus(key)}
                                          disabled={expired || isEditing}
                                          sx={V4_ICON_BUTTON_SMALL}
                                        >
                                          <Icon>{key.isActive ? 'toggle_on' : 'toggle_off'}</Icon>
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title={t('f-com-delete')} arrow>
                                      <IconButton
                                        size="small"
                                        color="secondary"
                                        disabled={isEditing}
                                        onClick={() => handleDeleteApiKey(key)}
                                        sx={V4_ICON_BUTTON_SMALL}
                                      >
                                        <Icon>delete</Icon>
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      ) : (
                        <Alert severity="info">{t('f-no-data')}</Alert>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              {/* Readers */}
              <Grid item xs={12}>
                <Paper sx={styles.card} elevation={1}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">{t('f-readers-title')}</Typography>
                        {readersBusy ? (
                          <CircularProgress size={18} thickness={4} />
                        ) : (
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={loadReaders}
                            aria-label={t('f-com-reload')}
                            sx={V4_ICON_BUTTON_SMALL}
                          >
                            <Icon fontSize="small">refresh</Icon>
                          </IconButton>
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      {readers.length > 0 ? (
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                              <th className="px-4 py-2 text-left font-semibold text-gray-700 text-xs">MAC</th>
                              <th className="px-4 py-2 text-left font-semibold text-gray-700 text-xs">
                                {t('f-com-status')}
                              </th>
                              <th className="px-4 py-2 text-left font-semibold text-gray-700 text-xs">
                                {t('f-reader-last-seen')}
                              </th>
                              <th className="px-4 py-2 text-left font-semibold text-gray-700 text-xs">
                                {t('f-reader-firmware')}
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {readers.map((reader) => (
                              <tr key={reader.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 py-2 font-mono text-xs text-gray-700">{reader.macAddress}</td>
                                <td className="px-4 py-2">
                                  <ReaderHealthBadge reader={reader} />
                                </td>
                                <td className="px-4 py-2 text-xs text-gray-500">
                                  {formatLastSeen(reader.lastHeartbeatAt)}
                                </td>
                                <td className="px-4 py-2 text-xs text-gray-500">{reader.lastFirmwareVersion ?? '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        !readersBusy && <Alert severity="info">{t('f-no-data')}</Alert>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </>
          )}
        </Grid>
      </Container>

      <GenerateApiKeyDialog open={generateDialogOpen} slaughterhouseId={id || ''} onClose={handleGenerateApiKeyClose} />
    </>
  );
};

export default SlaughterhouseManagementView;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (theme: Theme) =>
  ({
    paper: {
      padding: theme.spacing(2),
      display: 'flex',
      overflow: 'auto',
      flexDirection: 'column',
      minHeight: 200
    },
    bold: {
      fontWeight: 'bold'
    },
    card: {
      marginTop: theme.spacing(2),
      padding: theme.spacing(3),
      width: '100%'
    },
    mr1: {
      marginRight: theme.spacing(1)
    },
    mt2: {
      marginTop: theme.spacing(2)
    }
  }) as const;
